<script setup lang="ts">
import { formatDate } from '@/lib/formatters/date'
import { ref, watch } from 'vue'
import { RouterView, useRouter } from 'vue-router'

import { useAuthStore } from '@/stores/auth'
import { useCartStore } from '@/stores/cart'
import { useCompletedActionStore } from '@/stores/completedAction'
import { useNextOrderStore } from '@/stores/nextOrder'

import Loading from '@/components/Loading.vue'
import Toast from '@/components/Toast.vue'

const authStore = useAuthStore()
const router = useRouter()
const cartStore = useCartStore()
const completedActionStore = useCompletedActionStore()
const nextOrderStore = useNextOrderStore()

watch(
  () => authStore.user?.rechargeId,
  (rechargeId) => {
    if (rechargeId) {
      nextOrderStore.init(rechargeId)
    }
  },
  { immediate: true },
)
const storedCart = localStorage.getItem('cart')
if (storedCart) {
  const parsedCart = JSON.parse(storedCart)
  cartStore.$patch(parsedCart)
  cartStore.loadProducts()
}

cartStore.$subscribe((_, state) => {
  const serialisedState = JSON.stringify(state)
  localStorage.setItem('cart', serialisedState)
})

const routerIsReady = ref<boolean>(false)
router.isReady().then(() => {
  routerIsReady.value = true
})
</script>

<template>
  <div
    v-if="authStore.user && authStore.user.impersonating"
    class="AppWarningBanner"
  >
    <div class="AppWarningBanner__Message">
      <p>
        <b>Warning!</b>
        You are viewing the app as someone else. Be careful! Any changes you
        make will be applied to this persons's account. This session will last a
        maximum of 20 minutes.
      </p>
    </div>
  </div>
  <template v-if="routerIsReady">
    <RouterView v-slot="{ Component }">
      <Transition appear mode="out-in">
        <component :is="Component" />
      </Transition>
    </RouterView>
  </template>
  <template v-else>
    <div class="AppLoadingState">
      <Loading />
    </div>
  </template>
  <Toast
    v-if="
      completedActionStore.lastCompletedAction &&
      completedActionStore.background
    "
    :mode="['deep']"
    :has-close="true"
    @close="completedActionStore.clearCompletedAction()"
  >
    <template
      v-if="
        completedActionStore.lastCompletedAction.type ===
        'next_order_product_added'
      "
    >
      <p>
        We’ve added
        {{ completedActionStore.lastCompletedAction.data.quantity }}x
        {{ completedActionStore.lastCompletedAction.data.product.title }} to
        your order.
      </p>
    </template>
    <template
      v-else-if="
        completedActionStore.lastCompletedAction.type ===
        'next_order_item_edited'
      "
    >
      <p>
        We’ve updated your order to include
        {{ completedActionStore.lastCompletedAction.data.quantity }}x
        {{ completedActionStore.lastCompletedAction.data.title }}
        <template
          v-if="completedActionStore.lastCompletedAction.data.frequency === 1"
        >
          every month
        </template>
        <template
          v-else-if="completedActionStore.lastCompletedAction.data.frequency"
        >
          every
          {{ completedActionStore.lastCompletedAction.data.frequency }} months
        </template>
        .
      </p>
    </template>
    <template
      v-else-if="
        completedActionStore.lastCompletedAction.type === 'cart_item_added'
      "
    >
      <p>
        We’ve added
        {{ completedActionStore.lastCompletedAction.data.quantity }}x
        {{ completedActionStore.lastCompletedAction.data.product.title }} to
        your cart.
      </p>
    </template>
    <template
      v-else-if="
        completedActionStore.lastCompletedAction.type === 'subscription_updated'
      "
    >
      <p>
        We’ve updated your
        {{
          formatDate(
            'DD Mon',
            completedActionStore.lastCompletedAction.data.scheduledAt,
          )
        }}
        order to include
        {{ completedActionStore.lastCompletedAction.data.quantity }}x
        {{ completedActionStore.lastCompletedAction.data.title }}
        .
      </p>
    </template>
    <template
      v-else-if="
        completedActionStore.lastCompletedAction.type ===
        'payment_method_email_sent'
      "
    >
      <p>We’ve sent you an email with a link to update your payment details.</p>
    </template>
  </Toast>
</template>

<style>
.AppLoadingState {
  display: flex;
  width: 100vw;
  height: 100vh;
  align-items: center;
  justify-content: center;
}

.AppWarningBanner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background-color: var(--color-warning-100);
  height: 5px;
  z-index: 100;

  & .AppWarningBanner__Message {
    display: none;
    z-index: 1;
    position: absolute;
    top: 20px;
    left: 20px;
    right: 20px;
    background-color: var(--color-white);
    padding: 20px;
    border-radius: 8px;

    & p {
      margin: 0;
    }
  }

  &:hover .AppWarningBanner__Message {
    display: block;
  }
}
</style>
